import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { faTags, faSave, faTimes } from '@fortawesome/pro-solid-svg-icons';

import PageTemplate from 'Components/Templates/Page/Page/PageTemplate';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Container from 'Components/Atoms/Container/Container';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import TextField from 'Components/Organisms/TextField/TextField';
import Button from 'Components/Atoms/Button/Button';
import Notification from 'Components/Atoms/Notification/Notification';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';

import { BlogService } from '../../Services/Blog/BlogService';
import { BlogCategory } from '../../Api/Modules/Blog';
import { ROUTES } from 'Services/Route/Route.types';

const CategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const blogService = BlogService.getInstance();
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: ''
  });

  useEffect(() => {
    if (id) {
      loadData();
    } else {
      setInitialLoading(false);
    }
  }, [id]);

  const loadData = async () => {
    try {
      setInitialLoading(true);
      setError(null);

      const category = await blogService.getCategory(id!);
      
      console.log('📝 CategoryForm: Raw category data:', category);

      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || ''
      });
    } catch (err) {
      console.error('❌ CategoryForm: Error loading data', err);
      setError('Categorie laden mislukt.');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Naam is verplicht');
      return;
    }

    if (!formData.slug.trim()) {
      setError('Slug is verplicht');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (id) {
        await blogService.updateCategory(id, formData);
      } else {
        await blogService.createCategory(formData);
      }
      
      navigate('/blog/categories');
    } catch (err) {
      console.error('❌ CategoryForm: handleSubmit mislukt', err);
      setError(err instanceof Error ? err.message : `Categorie ${id ? 'bijwerken' : 'aanmaken'} mislukt`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/blog/categories');
  };

  if (initialLoading) {
    return (
      <PageTemplate>
        <Container padding='vertical'>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ 
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #9333ea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '16px', color: '#6b7280' }}>Categorie laden...</p>
          </div>
        </Container>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <Helmet>
        <title>{ ROUTES.BLOG.title }</title>
      </Helmet>
      
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
            <BreadcrumbItem size='small' path={ROUTES.BLOG.path}>Blog</BreadcrumbItem>
            <BreadcrumbItem size='small' path="/blog/categories">Categorieën</BreadcrumbItem>
            <BreadcrumbItem size='small'>{id ? 'Categorie bewerken' : 'Nieuwe categorie'}</BreadcrumbItem>
          </Breadcrumbs>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            backgroundColor: '#f3e8ff', 
            borderRadius: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <FontAwesomeIcon icon={faTags} size="lg" color="#9333ea" />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
              {id ? 'Blog categorie bewerken' : 'Nieuwe blog categorie'}
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
              {id ? 'Bewerk de blog categorie' : 'Maak een nieuwe blog categorie aan'}
            </p>
          </div>
        </div>
      </PageHeader>

      <Container padding='vertical'>
        {error && (
          <Notification variant='error' style={{ marginBottom: '24px' }}>
            {error}
          </Notification>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '24px' }}>
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Name */}
            <FormGroup title="Naam" isRequired>
              <TextField
                isEdit={true}
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                placeholder="Voer de naam van de categorie in"
              />
            </FormGroup>

            {/* Slug - Hidden but functional */}
            <input 
              type="hidden" 
              value={formData.slug} 
            />

            {/* Description */}
            <FormGroup title="Beschrijving">
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Optionele beschrijving van de categorie..."
              />
            </FormGroup>
          </div>

          {/* Form Actions */}
          <div style={{ 
            marginTop: '32px', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px' 
          }}>
            <Button
              variant='border'
              color='neutral-dark'
              size='medium'
              onClick={handleCancel}
              iconBefore={<FontAwesomeIcon icon={faTimes} />}
            >
              Annuleren
            </Button>
            <Button
              variant='solid'
              color='primary'
              size='medium'
              onClick={handleSubmit}
              isDisabled={loading}
              isLoading={loading}
              iconBefore={!loading && <FontAwesomeIcon icon={faSave} />}
            >
              {loading ? (id ? 'Bijwerken...' : 'Aanmaken...') : (id ? 'Categorie Bijwerken' : 'Categorie Aanmaken')}
            </Button>
          </div>
        </div>
      </Container>
    </PageTemplate>
  );
};

export default CategoryForm; 